// SearchResults.js - Premium Design
import React, { useEffect, useState, useMemo } from 'react';
import { useBooking } from '../hooks/useBooking';
import Layout from '../components/layout/Layout';
import ProgressSteps from '../components/common/ProgressSteps';
import VehicleCard from '../components/search/VehicleCard';
import RouteMap from '../components/search/RouteMap';
import BookingSummary from '../components/booking/BookingSummary';
import { PAGES, BOOKING_STEPS } from '../utils/constants';
import { formatTime, formatCurrency } from '../utils/helpers';
import PriceCalculationService from '../services/priceCalculator';

const SearchResults = () => {
  const {
    state: { searchData, selectedVehicle, routeInfo },
    setCurrentPage,
    setSelectedVehicle,
    setRouteInfo
  } = useBooking();

  const [localRouteInfo, setLocalRouteInfo] = useState(routeInfo || null);

  // Persist route info to context when calculated
  const handleRouteCalculated = React.useCallback((info) => {
    setLocalRouteInfo(info);
    setRouteInfo(info);
  }, [setRouteInfo]);

  // Dynamic Price for selected vehicle
  const selectedPriceResult = useMemo(() => {
    if (!selectedVehicle || !searchData?.searchFormData) return null;
    const sf = searchData.searchFormData;

    const toNum = (v) =>
      typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^\d.]/g, '')) || 0;

    const distanceKm = (localRouteInfo?.distanceKm ?? toNum(localRouteInfo?.distance))
      || (localRouteInfo?.distanceValue ? localRouteInfo.distanceValue / 1000 : 0)
      || toNum(sf.distance) || 0;
    
    const isHourly = sf.subService === 'hourly' || String(sf.type || '').toLowerCase().includes('hourly');

    const pickupAtDate = sf.pickupDate && sf.pickupTime ? `${sf.pickupDate}T${sf.pickupTime}` : new Date().toISOString();

    if (isHourly) {
      const hours = Number(sf.hours || selectedVehicle.pricing?.hours || 2);
      return PriceCalculationService.calculateHourlyPrice(selectedVehicle, hours, distanceKm, pickupAtDate);
    } else {
      const returnMultiplier = sf.showReturn ? 2 : 1;
      const waitingMins = Number(sf.waitingMins) || 0;
      return PriceCalculationService.calculateTransferPrice(selectedVehicle, distanceKm, pickupAtDate, {}, returnMultiplier, waitingMins);
    }
  }, [selectedVehicle, searchData, localRouteInfo]);

  const calculateDropoffTime = (pickupTime, durationSeconds) => {
    if (!pickupTime || !durationSeconds) return '';
    try {
      const [h, m] = pickupTime.split(':').map(Number);
      const date = new Date();
      date.setHours(h);
      date.setMinutes(m);
      date.setSeconds(0);
      const drop = new Date(date.getTime() + (durationSeconds || 0) * 1000);
      const hours = drop.getHours();
      const minutes = drop.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    if (searchData?.searchResults?.options?.length > 0 && !selectedVehicle) {
      setSelectedVehicle(searchData.searchResults.options[0]);
    }
  }, [searchData, selectedVehicle, setSelectedVehicle]);

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
  };

  const handleContinue = () => {
    setCurrentPage(PAGES.EXTRAS);
  };

  const handleNewSearch = () => {
    setCurrentPage(PAGES.HOME);
  };

  if (!searchData) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-8 rounded-3xl text-center">
            <p className="text-gray-400 mb-4 font-medium">No search data found</p>
            <button
              onClick={handleNewSearch}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-black text-xs tracking-widest uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
            >
              Start New Search
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showHeaderMobile={false}>
      <div className="min-h-screen pb-20 bg-[#050A12]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <ProgressSteps currentStep={BOOKING_STEPS.VEHICLE} />

          {/* Desktop & Table Grid Layout */}
          <div className="hidden md:grid grid-cols-12 gap-8 mt-4">
            {/* Main Content (Map + Vehicles) */}
            <div className="col-span-12 lg:col-span-8 space-y-8">
              {/* Map Section with Pills */}
              <div className="relative group">
                <div className="glass-panel rounded-[2rem] overflow-hidden border-white/5 shadow-2xl h-[400px]">
                  <RouteMap
                    searchData={searchData.searchFormData}
                    onRouteCalculated={handleRouteCalculated}
                  />
                </div>
                
                {/* Route Detail Pills */}
                {localRouteInfo && (
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-3 z-10">
                    <div className="bg-[#0f172a]/90 backdrop-blur-xl border border-white/10 px-6 py-2.5 rounded-2xl flex items-center gap-2.5 shadow-2xl">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">{localRouteInfo.duration}</span>
                    </div>
                    <div className="bg-[#0f172a]/90 backdrop-blur-xl border border-white/10 px-6 py-2.5 rounded-2xl flex items-center gap-2.5 shadow-2xl">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">{localRouteInfo.distance}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Vehicle Selection Roll */}
              <div className="pt-8 space-y-6">
                <div className="flex items-center justify-between px-4">
                  <h3 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.25em]">Available Classes</h3>
                  {selectedVehicle && (
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{searchData.searchResults?.options?.length} Rides found for your trip</span>
                  )}
                </div>
                
                <div className="space-y-3">
                  {searchData.searchResults?.options?.map((option, index) => (
                    <VehicleCard
                      key={index}
                      vehicle={option}
                      selected={selectedVehicle?.classId === option.classId}
                      onSelect={() => handleVehicleSelect(option)}
                      searchData={searchData.searchFormData}
                      routeInfo={localRouteInfo}
                    />
                  ))}
                </div>

                <div className="glass-panel bg-blue-500/5 border-blue-500/10 rounded-2xl p-5 flex items-center gap-5">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-xl">ℹ️</div>
                  <div>
                    <h4 className="font-black text-[10px] text-blue-500 uppercase tracking-widest">Premium Service Included</h4>
                    <p className="text-[11px] text-gray-500 mt-1 font-medium italic">
                      All our rides include professional English speaking chauffeurs, bottled water, and flight tracking.
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleNewSearch}
                  className="group flex items-center gap-3 px-6 py-3 text-blue-500 text-[10px] font-black uppercase tracking-widest hover:text-white transition-all"
                >
                  <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                  </svg>
                  Modify Search Parameters
                </button>
              </div>
            </div>

            {/* Sticky Sidebar */}
            <div className="hidden lg:block lg:col-span-4 h-fit sticky top-24">
              <BookingSummary
                searchData={searchData.searchFormData}
                selectedVehicle={selectedVehicle}
                step={BOOKING_STEPS.VEHICLE}
                routeInfo={localRouteInfo}
                onContinue={handleContinue}
              />
            </div>
          </div>

          {/* Mobile View with Bottom Overlay Ride Feed */}
          <div className="md:hidden space-y-6">
            <div className="relative -mx-4 -mt-8 h-[45vh] overflow-hidden">
              <RouteMap
                searchData={searchData.searchFormData}
                onRouteCalculated={handleRouteCalculated}
                className="w-full h-full"
              />
              <div className="absolute top-6 left-6 z-10">
                <button onClick={handleNewSearch} className="w-10 h-10 bg-[#0f172a]/90 backdrop-blur-md rounded-full border border-white/10 flex items-center justify-center text-white shadow-2xl">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              {localRouteInfo && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-blue-600 px-5 py-2 rounded-full text-[10px] font-black text-white shadow-2xl tracking-widest uppercase">
                  {localRouteInfo.duration} • {localRouteInfo.distance}
                </div>
              )}
            </div>

            <div className="space-y-4 pb-32">
              <h3 className="text-[10px] font-black text-blue-500 uppercase tracking-widest pl-4">Suggested Rides</h3>
              <div className="space-y-2.5">
                {searchData.searchResults?.options?.map((option, idx) => (
                  <VehicleCard
                    key={idx}
                    vehicle={option}
                    selected={selectedVehicle?.classId === option.classId}
                    onSelect={() => handleVehicleSelect(option)}
                    searchData={searchData.searchFormData}
                    routeInfo={localRouteInfo}
                    compact={true}
                  />
                ))}
              </div>
            </div>

            {/* Mobile Fixed Confirmation Bar */}
            <div className="fixed bottom-6 left-4 right-4 z-[100]">
              <div className="bg-[#0f172a]/95 backdrop-blur-2xl border border-white/10 rounded-full p-2 pl-6 flex items-center justify-between shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
                <div className="min-w-0">
                  <p className="text-[9px] text-blue-500 font-black uppercase tracking-widest italic truncate">{selectedVehicle?.className || 'Select a Ride'}</p>
                  <p className="text-lg font-black text-white tracking-tighter leading-none mt-0.5">
                    {selectedPriceResult ? formatCurrency(selectedPriceResult.total, selectedPriceResult.currency) : '--'}
                  </p>
                </div>
                <button 
                  onClick={handleContinue}
                  disabled={!selectedVehicle}
                  className="bg-blue-600 text-white h-12 px-8 rounded-full font-black text-xs uppercase tracking-[0.15em] transition-all active:scale-95 shadow-lg shadow-blue-500/20 disabled:opacity-30 disabled:pointer-events-none"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SearchResults;